import { faPause, faPlay, faStop } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet } from "react-router-dom";
import { useEffectOnce } from "usehooks-ts";

import Button from "../../components/button/Button";
import { OutlineButton } from "../../components/button/OutlineButton";
import { PageLayout } from "../../components/layouts/PageLayout";
import { TimePeriod } from "../../hooks/useDetectTimePeriod";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import {
  useFilterTimeQuery,
  useGetBilledHoursQuery,
  useLazyFilterTimeQuery,
} from "../../slices/UserSlice";
import { GetPageTitle } from "../../support/ScrollToTop";
import { ExcelExport } from "../../utils/ExportData";
import AddTimer from "../main/AddTimer";
import { isTimerActive, isTimerPaused } from "../main/timer/timerTarget";
import { useTimerTarget } from "../main/timer/useTimerTarget";
import { Time } from "./InvoiceTime";
import AddTimeEntry from "./AddTimeEntry";
import StartTimer from "./StartTimer";
import { createDefaultTimeFilter } from "./time-filter.utils";
import { TIME_EXPORT_MAX_ROWS, timeExportRows } from "./timeExport.utils";
import { TimesListView } from "./TimesListView";

const TIME_PAGE_SIZE = 20;

export const TimeView: React.FC<unknown> = () => {
  const { timer, pauseActiveTimer, toggleActiveTimer } = useTimerTarget();

  const [isOpenStart, setIsOpenStart] = useState(false);
  const [isOpenAddTime, setIsOpenAddTime] = useState(false);
  const [isOpenAddTimer, setIsOpenAddTimer] = useState(false);
  const [page, setPage] = useState(1);

  const orgId = getLinkedFirm()?.orgId || "";

  const [filter, setFilter] = useState<Time>(
    createDefaultTimeFilter({ orgId }),
  );

  const timeQueryArgs = useMemo(
    () => ({
      filterByClient: filter.filterByClient.map((item) => item.value),
      filterByAssignee: filter.filterByAssignee.map((item) => item.value),
      filterByWorkType: filter.filterByWorkType.map((item) => item.value),
      filterByStatus: filter.filterByStatus,
      filterByBillable: filter.filterByBillable ?? null,
      filterByDate:
        filter.filterByDate?.[0] === TimePeriod.All ? [] : filter.filterByDate,
      fromDate:
        filter.filterByDate?.[0] === TimePeriod.All
          ? null
          : filter.fromDate?.toISOString(),
      toDate:
        filter.filterByDate?.[0] === TimePeriod.All
          ? null
          : filter.toDate?.toISOString(),
      orgId,
      sortOrder: filter.sortOrder,
      sortBy: filter.sortBy,
      filterByWork: [],
      filterByWorkTask: [],
      pageNumber: page,
      pageSize: TIME_PAGE_SIZE,
    }),
    [filter, orgId, page],
  );

  const {
    data: filterTime,
    isLoading: isLoadingTime,
    isFetching: isFetchingTime,
    refetch: refetchTime,
  } = useFilterTimeQuery(timeQueryArgs, {
    skip: !orgId,
  });

  const { data: billableHours, refetch: refetchBilledHours } =
    useGetBilledHoursQuery(
      { orgId },
      {
        skip: !orgId,
      },
    );

  const closeAddTimer = () => {
    setIsOpenAddTimer(false);
  };

  const getData = useCallback(() => {
    if (!orgId) {
      return;
    }

    void refetchTime();
    void refetchBilledHours();
  }, [orgId, refetchBilledHours, refetchTime]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  useEffectOnce(() => {
    document.title = GetPageTitle("Time");
  });

  // QA batch 15 (TIME-029): export every entry matching the filters, not
  // just the page on screen.
  const [loadAllTime] = useLazyFilterTimeQuery();
  const loadExportRows = useCallback(async () => {
    const all = await loadAllTime({
      ...timeQueryArgs,
      pageNumber: 1,
      pageSize: TIME_EXPORT_MAX_ROWS,
    }).unwrap();
    return timeExportRows(all.items || []);
  }, [loadAllTime, timeQueryArgs]);

  const exportButton = (
    <div className={"flex items-center gap-2"}>
      <ExcelExport
        data={[]}
        loadData={loadExportRows}
        loadError={"Couldn't export the time entries. Try again."}
        fileName={"Time"}
      />
      {/* QA batch 15 (TIME-015): time already worked. */}
      <OutlineButton
        buttonType={"button"}
        label={"Add time"}
        colorType={"neutral"}
        onClick={() => setIsOpenAddTime(true)}
        extraClasses={"normal-case"}
      />
    </div>
  );

  const buttonTimerVariant = () => {
    if (!isTimerActive(timer)) {
      return (
        <div className={"flex items-center gap-2"}>
          {exportButton}

          <Button
            onClick={() => setIsOpenStart(true)}
            buttonType={"button"}
            label={"Start timer"}
            extraClasses={"normal-case"}
          />
        </div>
      );
    }

    return (
      <div className={"flex items-center gap-3"}>
        {exportButton}

        <button
          type={"button"}
          onClick={toggleActiveTimer}
          className={
            "flex h-[32px] w-[32px] items-center justify-center rounded-full bg-blue-500 text-sm text-white"
          }
          aria-label={isTimerPaused(timer) ? "Continue timer" : "Pause timer"}>
          <FontAwesomeIcon icon={isTimerPaused(timer) ? faPlay : faPause} />
        </button>

        <button
          type={"button"}
          onClick={() => {
            if (!isTimerPaused(timer)) {
              pauseActiveTimer();
            }

            setIsOpenAddTimer(true);
          }}
          className={
            "flex h-[32px] w-[32px] items-center justify-center rounded-full bg-red-500 text-sm text-white"
          }
          aria-label={"Stop timer"}>
          <FontAwesomeIcon icon={faStop} />
        </button>

        <Button
          buttonType={"button"}
          label={"Start timer"}
          disabled
          colorType={"neutral"}
          extraClasses={
            "normal-case border-none bg-gray-200 text-gray-500 hover:cursor-not-allowed"
          }
        />
      </div>
    );
  };

  return (
    <>
      <PageLayout
        title={"Time"}
        extraClasses={"overflow-visible"}
        titleClasses={"mb-6"}>
        <TimesListView
          buttonTimerVariant={buttonTimerVariant}
          billableHours={billableHours}
          filterTime={filterTime?.items}
          getData={getData}
          filter={filter}
          setFilter={setFilter}
          isLoading={isLoadingTime || isFetchingTime}
          page={filterTime?.pageNumber ?? page}
          totalPages={filterTime?.totalPages ?? 1}
          total={filterTime?.totalCount}
          pageSize={filterTime?.pageSize || TIME_PAGE_SIZE}
          onPageChange={setPage}
        />

        <div>
          <Outlet />
        </div>
      </PageLayout>

      {isOpenStart && (
        <StartTimer
          isOpen={isOpenStart}
          closeModal={() => setIsOpenStart(false)}
        />
      )}

      {isOpenAddTime && (
        <AddTimeEntry
          closeModal={() => setIsOpenAddTime(false)}
          onSaved={getData}
        />
      )}

      {isOpenAddTimer && (
        <AddTimer isOpen={isOpenAddTimer} closeModal={closeAddTimer} />
      )}
    </>
  );
};
